import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, X, Loader2, CheckCircle2, Radio, Square, Car, ArrowRight, ArrowDown, CornerUpLeft, Clock } from 'lucide-react';
import { buyerActivityAPI } from '../../lib/api';
import { fmtCurrency, fmtNumber } from '../../lib/financeMath';
import { LiveNowBadge, ListeningDots, useLiveBuyers } from './LiveNow';

/**
 * Buyer Activity — ONE buyer's visit, in the STAFF member's own language.
 *
 * The backend hands over `event_type` + a small `params` object and nothing
 * pre-worded, so every line below is composed here from the staff member's
 * EN/FR/ES locale — independent of whichever language the buyer used.
 *
 * CHRONOLOGICAL, oldest first: the visit reads like a story from the top
 * ("Tapped the card") downwards, with each new action added BELOW the last —
 * the same order live and when the history is opened afterwards.
 */

// Relative time is the PRIMARY display; the exact local timestamp is the title
// attribute, so it is one hover/long-press away without cluttering the line.
const rel = (iso, t) => {
    try {
        const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return t('buyerActivity.now', 'just now');
        const m = Math.floor(s / 60);
        if (m < 60) return t('buyerActivity.minsAgo', '{{n}} mins ago', { n: m });
        const h = Math.floor(m / 60);
        if (h < 24) return t('buyerActivity.hoursAgo', '{{n}} hours ago', { n: h });
        return t('buyerActivity.daysAgo', '{{n}} days ago', { n: Math.floor(h / 24) });
    } catch { return ''; }
};

const exact = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const LABELS = {
    'vehicle.tap': ['buyerActivity.act.tap', 'Tapped the card'],
    'vehicle.carfax_viewed': ['buyerActivity.act.carfax', 'Checked CARFAX'],
    'vehicle.fi_calculator_opened': ['buyerActivity.act.calcOpened', 'Opened the payment calculator'],
    'vehicle.fi_payment_estimated': ['buyerActivity.act.paymentEstimated', 'Verified Monthly Payments'],
    'vehicle.fi_prequal_submitted': ['buyerActivity.act.prequal', 'Applied for financing'],
    'vehicle.fi_warranty_interest': ['buyerActivity.act.warranty', 'Flagged interest in Extended Warranty'],
    'vehicle.insurance_quote_requested': ['buyerActivity.act.insurance', 'Requested an Insurance Quote'],
    'vehicle.trade_in_submitted': ['buyerActivity.act.tradeIn', 'Checked Trade-In Value'],
    // IN PROGRESS — they are doing it right now, it is not done.
    'vehicle.trade_in_opened': ['buyerActivity.act.tradeInOpened', 'Started a trade-in appraisal'],
    'vehicle.insurance_opened': ['buyerActivity.act.insuranceOpened', 'Started an insurance enquiry'],
    'vehicle.details_viewed': ['buyerActivity.act.detailsViewed', 'Reading the vehicle details'],
    'vehicle.form_progress': ['buyerActivity.act.formProgress', 'Filling in a form'],
    'vehicle.trade_in_photos_added': ['buyerActivity.act.tradeInPhotos', 'Added photos of their trade-in'],
    'vehicle.contact_sales': ['buyerActivity.act.contactSales', 'Contacted Sales'],
    'vehicle.test_drive_requested': ['buyerActivity.act.testDrive', 'Requested a test drive'],
    'vehicle.saved': ['buyerActivity.act.saved', 'Saved this vehicle'],
    'vehicle.share_created': ['buyerActivity.act.shared', 'Sent this vehicle to someone'],
    'vehicle.special_offer_click': ['buyerActivity.act.offerClick', 'Opened the special offer'],
};

// An IN-PROGRESS line is amber and says so. A salesperson reading "Filling in
// their trade-in" must never mistake it for "submitted a trade-in" — the
// backend owns the distinction (`in_progress` on every item) so it cannot
// drift between screens.
const labelFor = (item, t) => {
    if (item.event_type === 'vehicle.form_progress') {
        const screen = (item.params || {}).screen;
        if (screen === 'trade_in') return t('buyerActivity.act.tradeInProgress', 'Filling in their trade-in');
        if (screen === 'finance') return t('buyerActivity.act.financeProgress', 'Filling in the financing form');
        return t('buyerActivity.act.formProgress', 'Filling in a form');
    }
    // Money gets its own title. "Contacted Sales" over an amount made the two
    // highest-intent actions read like an ordinary enquiry; a salesperson has to
    // see WHICH it was at a glance. Plain enquiries keep "Contacted Sales".
    if (item.event_type === 'vehicle.contact_sales') {
        const rt = (item.params || {}).request_type;
        if (rt === 'vehicle_deposit' || rt === 'deposit') return t('buyerActivity.act.leftDeposit', 'Left a deposit');
        if (rt === 'vehicle_offer' || rt === 'offer') return t('buyerActivity.act.madeOffer', 'Made an offer');
    }
    const [key, fallback] = LABELS[item.event_type] || [null, item.event_type];
    return key ? t(key, fallback) : fallback;
};

// For callers outside this file (the live-buyers list) that have an event type
// but no item — one source of wording for the same action.
export const actionLabelFor = (eventType, t, requestType = null) =>
    labelFor({ event_type: eventType, params: requestType ? { request_type: requestType } : {} }, t);

const money = (n) => {
    if (n === null || n === undefined || n === '') return null;
    const v = Number(n);
    if (!Number.isFinite(v)) return null;
    // Locale-aware: "48 000 $" in French, "$48,000" in English.
    return fmtCurrency(v);
};

// Visual weight, not decoration. A salesperson glancing for three seconds has
// to come away knowing what this buyer DID, not that they browsed. So the
// things a buyer had to fill in a form to do outrank the things they merely
// looked at, and the passive steps recede instead of competing.
const KEY = new Set([
    'vehicle.trade_in_submitted',
    'vehicle.fi_prequal_submitted',
    'vehicle.test_drive_requested',
    'vehicle.contact_sales',
    'vehicle.insurance_quote_requested',
]);
const PASSIVE = new Set([
    'vehicle.tap',
    'vehicle.carfax_viewed',
    'vehicle.fi_calculator_opened',
    'vehicle.special_offer_click',
    'vehicle.details_viewed',
]);
const tierOf = (et) => (KEY.has(et) ? 'key' : PASSIVE.has(et) ? 'passive' : 'normal');

// Above this many entries a full list stops being scannable, so the passive
// browsing steps fold away by default. Nothing is ever removed — one tap
// brings them back.
const FOLD_ABOVE = 8;

// The one-line detail that sits UNDER the action. Returns null for anything
// with nothing real behind it, so a bare action never grows an empty line.
function detailFor(item, t) {
    const p = item.params || {};
    switch (item.event_type) {
        case 'vehicle.form_progress': {
            if (p.screen === 'trade_in') {
                const bits = [
                    [p.year, p.make, p.model, p.trim].filter(Boolean).join(' ') || null,
                    p.mileage ? t('buyerActivity.km', '{{n}} km', { n: fmtNumber(String(p.mileage).replace(/\D/g, '') || 0) }) : null,
                    p.condition ? t(`buyerActivity.cond.${p.condition}`, p.condition) : null,
                    p.payoff ? t('buyerActivity.payoff', '{{amount}} still owing', { amount: money(String(p.payoff).replace(/[^\d.]/g, '')) }) : null,
                    p.photos ? t('buyerActivity.photos', { count: Number(p.photos) }) : null,
                ].filter(Boolean);
                return bits.length ? bits.join(' · ') : null;
            }
            return p.fields
                ? t('buyerActivity.fieldsFilled', '{{n}} fields filled so far', { n: p.fields })
                : null;
        }
        case 'vehicle.trade_in_photos_added':
            return p.photos ? t('buyerActivity.photos', { count: Number(p.photos) }) : null;
        case 'vehicle.trade_in_submitted': {
            const bits = [
                [p.year, p.make, p.model, p.trim].filter(Boolean).join(' ') || null,
                p.mileage ? t('buyerActivity.km', '{{n}} km', { n: fmtNumber(p.mileage) }) : null,
                p.condition ? t(`buyerActivity.cond.${p.condition}`, p.condition) : null,
                p.payoff ? t('buyerActivity.payoff', '{{amount}} still owing', { amount: money(p.payoff) }) : null,
                p.photos ? t('buyerActivity.photos', { count: Number(p.photos) }) : null,
            ].filter(Boolean);
            return bits.length ? bits.join(' · ') : null;
        }
        case 'vehicle.fi_payment_estimated': {
            const amt = money(p.amount);
            if (!amt) return null;
            return [
                `${amt} ${t(`buyerActivity.freq.${p.frequency || 'monthly'}`, p.frequency || 'monthly')}`,
                p.term_months ? t('buyerActivity.months', '{{n}} months', { n: p.term_months }) : null,
                p.down_payment ? t('buyerActivity.down', '{{amount}} down', { amount: money(p.down_payment) }) : null,
            ].filter(Boolean).join(' · ');
        }
        case 'vehicle.fi_prequal_submitted': {
            const amt = money(p.amount);
            return amt ? t('buyerActivity.prequalDetail', 'Estimated payment {{amount}}', { amount: amt }) : null;
        }
        case 'vehicle.fi_warranty_interest':
            return p.plan_label || null;
        case 'vehicle.contact_sales': {
            if (p.amount) {
                // The title already says offer vs deposit, so the detail is
                // just the amount — "Made an offer / $48,000".
                return money(p.amount);
            }
            return p.excerpt ? `“${p.excerpt}”` : null;
        }
        case 'vehicle.test_drive_requested':
            return [p.date, p.time].filter(Boolean).join(' · ') || null;
        default:
            return null;
    }
}

// Live-watch poll cadence. Matches the buyer page's own 5s tick closely enough
// that a staff member sees an action land within one cycle, without inventing a
// second, faster cadence than the architecture already runs at.
const WATCH_POLL_MS = 6000;

export default function BuyerActivityPanel({ conversationId: conversationIdProp, open, onClose,
                                            autoGoLive = false,
                                            // SESSION MODE: addressed by the buyer's own session
                                            // instead of a conversation, so a buyer who has only
                                            // tapped a card is reachable. The conversation path is
                                            // unchanged and still the shortcut from inside a thread.
                                            sessionId: sessionIdProp = null,
                                            organizationId = null,
                                            title = null }) {
    const { t } = useTranslation();
    // A "Same visit" jump swaps which conversation this panel is reading, and
    // offers the way back. Each vehicle keeps its OWN thread — this navigates
    // between them, it never merges them.
    const [jumped, setJumped] = useState(null);
    const conversationId = jumped?.conversation_id || conversationIdProp;
    const sessionMode = !conversationId && !!sessionIdProp;
    const [sameVisit, setSameVisit] = useState(null);
    const [visits, setVisits] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [orgId, setOrgId] = useState(null);
    // Presence in session mode comes back on the timeline response itself —
    // there is no conversation id to look up on the board-wide badge call.
    const [sessionLive, setSessionLive] = useState(false);
    const ready = !!conversationId || sessionMode;
    // ONE fetcher for both modes: identical payload, identical rules, so
    // live-watching and reading the history afterwards stay one code path.
    const fetchTimeline = useCallback(
        (since = null) => (sessionMode
            ? buyerActivityAPI.sessionTimeline(organizationId, sessionId, since)
            : buyerActivityAPI.timeline(conversationId, sessionId, since)),
        [sessionMode, organizationId, conversationId, sessionId]);

    // GO LIVE. `feed` is what has arrived SINCE the watch started — appended
    // under the historical timeline so the whole visit reads top to bottom in
    // one direction.
    const [watching, setWatching] = useState(false);
    const [feed, setFeed] = useState([]);
    const [leftAt, setLeftAt] = useState(null);
    const [ticking, setTicking] = useState(false);
    const cursorRef = useRef(null);
    const feedEndRef = useRef(null);

    // While NOT watching, presence comes from the same one-call board lookup the
    // Sales Desk badges use, so an idle open panel costs one small request per
    // tick instead of re-reading the timeline to find out if anyone is there.
    const liveIds = useLiveBuyers(orgId, open && !watching && !!conversationId);
    const [watchLive, setWatchLive] = useState(false);
    const isLive = watching ? watchLive : (sessionMode ? sessionLive : liveIds.has(conversationId));

    // Up to three, newest first. Financing terms count as a highlight even
    // though nothing was submitted: a buyer who settled on a payment has told
    // you their budget, which is the most useful thing on the screen.
    const highlights = (items || [])
        .filter((it) => KEY.has(it.event_type) || it.event_type === 'vehicle.fi_payment_estimated')
        .slice(0, 3);
    const long = (items || []).length > FOLD_ABOVE;
    // The API answers newest-first (that is what the `since` cursor needs).
    // The SCREEN is chronological: first action at the top, newest at the
    // bottom, so the salesperson reads the visit as it happened.
    const shown = (items || []).filter(
        (it) => showAll || !long || tierOf(it.event_type) !== 'passive').slice().reverse();
    const hidden = (items || []).length - shown.length;

    const loadVisits = useCallback(async () => {
        setError(null);
        try {
            const r = await buyerActivityAPI.visits(conversationId);
            const v = r.data?.visits || [];
            setVisits(v);
            setOrgId(r.data?.organization_id || null);
            setSessionId((v.find((x) => x.is_primary) || v[0])?.session_id || null);
        } catch (e) {
            setVisits([]);
            setError(e?.response?.data?.detail || t('common.error', 'Something went wrong'));
        }
    }, [conversationId, t]);

    useEffect(() => { if (open && conversationId) loadVisits(); }, [open, conversationId, loadVisits]);

    // Session mode needs no visit lookup: the caller already knows exactly
    // which buyer it is looking at, because that is what it clicked.
    useEffect(() => {
        if (!open || !sessionMode) return;
        setVisits([]);
        setOrgId(organizationId);
        setSessionId(sessionIdProp);
    }, [open, sessionMode, organizationId, sessionIdProp]);

    useEffect(() => {
        if (!open || !ready || !sessionId) { setItems(sessionId ? null : []); return; }
        let alive = true;
        setItems(null);
        fetchTimeline()
            .then((r) => {
                if (!alive) return;
                const list = r.data?.items || [];
                setItems(list);
                setSessionLive(!!r.data?.live);
                // Where the live feed picks up from, so the first tick can
                // never re-show something already on the screen.
                cursorRef.current = list[0]?.at || new Date().toISOString();
            })
            .catch((e) => {
                if (!alive) return;
                setItems([]);
                setError(e?.response?.data?.detail || t('common.error', 'Something went wrong'));
            });
        return () => { alive = false; };
    }, [open, ready, sessionId, fetchTimeline, t]);

    const startWatching = useCallback(() => {
        cursorRef.current = cursorRef.current || new Date().toISOString();
        setFeed([]);
        setLeftAt(null);
        setWatchLive(true);
        setWatching(true);
    }, []);

    const stopWatching = useCallback(async () => {
        setWatching(false);
        setTicking(false);
        // Fold what arrived live back into the historical timeline, so the
        // static view the salesperson returns to is not missing the last
        // few minutes.
        if (ready && sessionId) {
            try {
                const r = await fetchTimeline();
                setItems(r.data?.items || []);
                cursorRef.current = (r.data?.items || [])[0]?.at || cursorRef.current;
            } catch { /* keep what is on screen */ }
        }
        setFeed([]);
    }, [ready, sessionId, fetchTimeline]);

    // THE LIVE POLL. Incremental by construction (`since`), so a tick costs one
    // small response rather than the whole visit. Stops itself the moment the
    // buyer's heartbeat goes stale — there is nothing to watch on a session
    // that has gone away, and polling it forever would be a lie on screen.
    useEffect(() => {
        if (!watching || !ready || !sessionId) return;
        let alive = true;
        const tick = async () => {
            if (document.hidden) return;
            setTicking(true);
            try {
                const r = await fetchTimeline(cursorRef.current);
                if (!alive) return;
                const fresh = (r.data?.items || []).slice().reverse();   // oldest → newest
                if (fresh.length) {
                    cursorRef.current = fresh[fresh.length - 1].at;
                    setFeed((f) => [...f, ...fresh]);
                }
                setWatchLive(!!r.data?.live);
                if (!r.data?.live) {
                    setLeftAt(r.data?.last_seen_at || new Date().toISOString());
                    setWatching(false);
                }
            } catch (e) {
                if (!alive) return;
                setError(e?.response?.data?.detail || t('common.error', 'Something went wrong'));
                setWatching(false);
            } finally {
                if (alive) setTicking(false);
            }
        };
        tick();
        const iv = setInterval(tick, WATCH_POLL_MS);
        return () => { alive = false; clearInterval(iv); };
    }, [watching, ready, sessionId, fetchTimeline, t]);

    // Nothing polls unattended: closing the panel or unmounting the screen
    // ends the watch, it does not leave a timer running in the background.
    useEffect(() => {
        if (!open) { setWatching(false); setFeed([]); setLeftAt(null); setTicking(false); }
    }, [open]);

    // Split-screen / demo entry: land already watching instead of making the
    // presenter click Go Live in front of a prospect.
    useEffect(() => {
        if (autoGoLive && open && isLive && !watching && !leftAt && items !== null) startWatching();
    }, [autoGoLive, open, isLive, watching, leftAt, items, startWatching]);

    // AUTO-SCROLL, but never over the salesperson's shoulder: we follow the
    // newest action only while they are already at the bottom. The moment they
    // scroll up to read something, we stop moving the page and offer a
    // "New activity" button instead.
    const scrollRef = useRef(null);
    const pinnedRef = useRef(true);
    const [unseen, setUnseen] = useState(0);

    const toBottom = useCallback((behavior = 'smooth') => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior });
        pinnedRef.current = true;
        setUnseen(0);
    }, []);

    const onScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        pinnedRef.current = atBottom;
        if (atBottom) setUnseen(0);
    }, []);

    useEffect(() => {
        if (!feed.length) return;
        if (pinnedRef.current) toBottom();
        else setUnseen((n) => n + 1);
    }, [feed.length, toBottom]);

    // A fresh open (or a switch to another visit) starts at the beginning of
    // the story, not mid-scroll from the previous buyer.
    useEffect(() => { pinnedRef.current = true; setUnseen(0); }, [sessionId, conversationId, open]);

    // What else this buyer looked at on the same walk around the lot.
    useEffect(() => {
        if (!open || !ready || !sessionId) { setSameVisit(null); return; }
        let alive = true;
        const req = sessionMode
            ? buyerActivityAPI.sessionSameVisit(organizationId, sessionId)
            : buyerActivityAPI.sameVisit(conversationId, sessionId);
        req
            .then((r) => { if (alive) setSameVisit(r.data?.vehicles || []); })
            .catch(() => { if (alive) setSameVisit([]); });
        return () => { alive = false; };
    }, [open, ready, sessionMode, organizationId, conversationId, sessionId]);

    // Closing the panel forgets the jump, so it reopens where the salesperson
    // actually is rather than on whatever they wandered off to last time.
    useEffect(() => { if (!open) setJumped(null); }, [open]);

    if (!open) return null;

    return (
        // No full-screen backdrop: it would swallow the first tap aimed at the
        // controls underneath. The wrapper passes taps straight through and the
        // sheet starts below the desk header, so Cameras / Back / Sales Lot stay
        // reachable while it is open. `md:` = full page; a split-screen pane is
        // always narrower than that.
        <div className="fixed inset-0 z-[70] flex justify-end pointer-events-none pt-[200px] md:pt-16"
            data-testid="buyer-activity-panel">
            <aside ref={scrollRef} onScroll={onScroll}
                className="pointer-events-auto h-full w-[88%] md:w-full md:max-w-md bg-slate-950 border-l border-white/10
                              overflow-y-auto overscroll-contain shadow-2xl shadow-black/60
                              animate-in slide-in-from-right duration-200">
                <header className="sticky top-0 z-10 flex items-center gap-2.5 px-4 py-3.5 bg-slate-950/95 backdrop-blur border-b border-white/10">
                    <Activity className="w-4 h-4 text-emerald-400 shrink-0" />
                    <h2 className="text-base font-bold text-white flex-1 min-w-0 truncate">
                        {jumped ? (jumped.label || jumped.card_id)
                            : (title || t('buyerActivity.title', 'Buyer Activity'))}
                    </h2>
                    {isLive && <LiveNowBadge testid="buyer-activity-live-badge" />}
                    <button type="button" onClick={onClose} data-testid="buyer-activity-close"
                        aria-label={t('common.close', 'Close')}
                        className="-mr-2 w-11 h-11 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </header>

                {jumped && (
                    <div className="px-4 pt-3.5">
                        <button type="button" onClick={() => setJumped(null)}
                            data-testid="buyer-activity-same-visit-back"
                            className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/[0.04]
                                       px-3 min-h-[40px] text-[12px] font-semibold text-slate-200
                                       hover:bg-white/[0.08] transition-colors">
                            <CornerUpLeft className="w-3.5 h-3.5" />
                            {t('sameVisit.back', 'Back to the vehicle they contacted you about')}
                        </button>
                    </div>
                )}

                {/* SAME VISIT — the connection between per-vehicle threads,
                    surfaced without merging them into one confused timeline. */}
                {sameVisit && sameVisit.length > 0 && (
                    <div className="px-4 pt-3.5" data-testid="buyer-activity-same-visit">
                        <div className="rounded-2xl border border-sky-500/25 bg-sky-500/[0.06] px-3.5 py-3">
                            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-sky-300/90">
                                {t('sameVisit.title', 'Same visit')}
                            </p>
                            <p className="mt-1 text-[12px] text-slate-400 leading-relaxed">
                                {t('sameVisit.hint', 'They also looked at these on the same visit.')}
                            </p>
                            <div className="mt-2.5 space-y-1.5">
                                {sameVisit.map((sv) => {
                                    const jumpable = !!sv.conversation_id;
                                    return (
                                        <button key={sv.card_id} type="button"
                                            disabled={!jumpable}
                                            onClick={() => jumpable && setJumped(sv)}
                                            data-testid={`buyer-activity-same-visit-${sv.card_id}`}
                                            title={jumpable ? '' : t('sameVisit.noThread', 'Only browsed — no conversation to open')}
                                            className={`w-full flex items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors ${
                                                jumpable ? 'hover:bg-white/[0.07] active:bg-white/[0.11]' : 'opacity-60 cursor-default'}`}>
                                            {sv.photo ? (
                                                <img src={sv.photo} alt="" loading="lazy"
                                                    className="w-12 h-9 rounded-lg object-cover bg-slate-800 shrink-0" />
                                            ) : (
                                                <span className="w-12 h-9 rounded-lg bg-slate-800 shrink-0 flex items-center justify-center">
                                                    <Car className="w-3.5 h-3.5 text-slate-600" />
                                                </span>
                                            )}
                                            <span className="flex-1 min-w-0">
                                                <span className="block text-[13px] font-semibold text-slate-100 truncate">
                                                    {sv.label || sv.card_id}
                                                </span>
                                                <span className="block text-[11px] text-slate-500 truncate">
                                                    {sv.stock_number ? `#${sv.stock_number} · ` : ''}
                                                    {t('sameVisit.actions', '{{count}} actions', { count: sv.event_count })}
                                                </span>
                                            </span>
                                            {jumpable && <ArrowRight className="w-4 h-4 text-sky-300/70 shrink-0" />}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* GO LIVE. An activity feed that refreshes itself — the copy
                    never implies a camera, because there is no camera. */}
                <div className="px-4 pt-3.5" data-testid="buyer-activity-live-bar">
                    {watching ? (
                        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/[0.07] px-3.5 py-3">
                            <div className="flex items-center gap-2.5">
                                <Radio className="w-4 h-4 text-rose-300 shrink-0" />
                                <span className="text-[13px] font-semibold text-rose-100 flex-1 min-w-0">
                                    {t('liveActivity.watchingTitle', 'Live activity — updating as they tap')}
                                </span>
                                {ticking ? <ListeningDots /> : <ListeningDots className="opacity-40" />}
                            </div>
                            <button type="button" onClick={stopWatching} data-testid="buyer-activity-stop-live"
                                className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/15
                                           bg-white/[0.04] hover:bg-white/[0.08] min-h-[44px] text-[13px] font-semibold
                                           text-slate-200 transition-colors">
                                <Square className="w-3.5 h-3.5" />{t('liveActivity.stop', 'Stop watching')}
                            </button>
                        </div>
                    ) : leftAt ? (
                        <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.07] px-3.5 py-3"
                            data-testid="buyer-activity-left">
                            <p className="text-[13px] font-semibold text-amber-100">
                                {t('liveActivity.buyerLeft', 'Buyer left — showing last known activity')}
                            </p>
                            <p className="mt-1 text-[11px] text-amber-200/60">
                                {t('liveActivity.lastSeen', 'Last seen {{when}}', { when: rel(leftAt, t) })}
                            </p>
                        </div>
                    ) : (
                        <button type="button" onClick={startWatching} disabled={!isLive}
                            data-testid="buyer-activity-go-live"
                            title={isLive ? '' : t('liveActivity.offlineHint', 'This buyer is not on the page right now')}
                            className={`w-full inline-flex items-center justify-center gap-2 rounded-xl min-h-[44px]
                                        text-[13px] font-bold transition-colors ${
                                isLive
                                    ? 'bg-rose-600 hover:bg-rose-500 text-white'
                                    : 'border border-white/10 bg-white/[0.02] text-slate-600 cursor-not-allowed'}`}>
                            <Radio className="w-4 h-4" />{t('liveActivity.goLive', 'Go Live')}
                        </button>
                    )}
                </div>

                <div className="px-4 pt-4 pb-[max(6rem,calc(env(safe-area-inset-bottom)+6rem))] space-y-5">
                    {error && (
                        <p className="text-[13px] text-amber-400" data-testid="buyer-activity-error">{error}</p>
                    )}

                    {/* Visits, not one merged history: two visits days apart read
                        as nonsense on a single timeline. */}
                    {(visits?.length || 0) > 1 && (
                        <div className="flex flex-wrap gap-2" data-testid="buyer-activity-visits">
                            {visits.map((v, i) => (
                                <button key={v.session_id} type="button"
                                    onClick={() => setSessionId(v.session_id)}
                                    data-testid={`buyer-activity-visit-${i}`}
                                    className={`min-h-[40px] rounded-full px-3.5 text-[12px] font-semibold border transition-colors ${
                                        v.session_id === sessionId
                                            ? 'border-emerald-500 bg-emerald-500/15 text-white'
                                            : 'border-slate-700 bg-slate-900 text-slate-400 active:bg-slate-800'}`}>
                                    {t('buyerActivity.visitN', 'Visit {{n}}', { n: visits.length - i })}
                                    {v.started_at && <span className="ml-1 font-normal opacity-70">
                                        {new Date(v.started_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                    </span>}
                                </button>
                            ))}
                        </div>
                    )}

                    {items === null ? (
                        <div className="flex items-center gap-2 text-[13px] text-slate-400 py-12 justify-center"
                            data-testid="buyer-activity-loading">
                            <Loader2 className="w-4 h-4 animate-spin" />{t('common.loading', 'Loading…')}
                        </div>
                    ) : items.length === 0 ? (
                        <p className="text-[13px] text-slate-500 py-12 text-center" data-testid="buyer-activity-empty">
                            {t('buyerActivity.empty', 'Nothing recorded for this visit yet.')}
                        </p>
                    ) : (
                        <>
                            {/* THE THREE-SECOND READ. The two or three things
                                that actually matter, before a single timeline
                                line is read. */}
                            {highlights.length > 0 && (
                                <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] px-4 py-3.5"
                                    data-testid="buyer-activity-highlights">
                                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-400/80 mb-2.5">
                                        {t('buyerActivity.highlights', 'What matters')}
                                    </p>
                                    <ul className="space-y-2">
                                        {highlights.map((it) => {
                                            const detail = it.has_detail ? detailFor(it, t) : null;
                                            return (
                                                <li key={`hl-${it.id}`} className="flex gap-2.5"
                                                    data-testid="buyer-activity-highlight">
                                                    <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                                                    <span className="text-sm leading-relaxed text-white">
                                                        <span className="font-semibold">{labelFor(it, t)}</span>
                                                        {detail && <span className="text-emerald-100/70"> — {detail}</span>}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}

                            <ul className="space-y-6" data-testid="buyer-activity-list">
                                {shown.map((it) => {
                                    const detail = it.has_detail ? detailFor(it, t) : null;
                                    const tier = tierOf(it.event_type);
                                    const wip = !!it.in_progress;
                                    return (
                                        <li key={it.id} data-testid={`buyer-activity-item-${it.event_type}`}
                                            data-inprogress={wip ? '1' : '0'}
                                            className={tier === 'key' ? 'border-l-2 border-emerald-500/50 pl-3 -ml-3'
                                                : wip ? 'border-l-2 border-amber-500/50 pl-3 -ml-3' : ''}>
                                            <div className="flex items-baseline gap-2">
                                                <span className={`flex-1 min-w-0 ${
                                                    tier === 'key'
                                                        ? 'text-[17px] font-bold text-white leading-snug'
                                                        : wip
                                                            ? 'text-[15px] font-semibold text-amber-100 leading-snug'
                                                            : tier === 'passive'
                                                                ? 'text-[15px] font-normal text-slate-400 leading-snug'
                                                                : 'text-[15px] font-semibold text-slate-100 leading-snug'}`}>
                                                    {labelFor(it, t)}
                                                </span>
                                                {/* Secondary by construction: never allowed to pull the
                                                    eye away from the action text, and kept on ONE line so
                                                    a wrapping action never orphans its timestamp. */}
                                                <span className="flex items-center gap-1.5 shrink-0 whitespace-nowrap">
                                                    {wip ? (
                                                        <Clock className="w-3 h-3 text-amber-400/80"
                                                            data-testid="buyer-activity-inprogress" />
                                                    ) : (
                                                        <CheckCircle2 className="w-3 h-3 text-emerald-500/50"
                                                            data-testid="buyer-activity-check" />
                                                    )}
                                                    <span className={`text-[12px] font-normal ${wip ? 'text-amber-300/70' : 'text-slate-500'}`}
                                                        title={exact(it.at)}
                                                        data-testid="buyer-activity-time">
                                                        {wip ? t('buyerActivity.inProgress', 'in progress · {{when}}', { when: rel(it.at, t) }) : rel(it.at, t)}
                                                    </span>
                                                </span>
                                            </div>
                                            {detail && (
                                                <p className="mt-1.5 ml-0.5 pl-3 border-l border-white/10 text-[13px] text-slate-400 leading-relaxed"
                                                    data-testid="buyer-activity-detail">{detail}</p>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>

                            {hidden > 0 && (
                                <button type="button" onClick={() => setShowAll((s) => !s)}
                                    data-testid="buyer-activity-toggle-passive"
                                    className="w-full rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06]
                                               min-h-[44px] text-[13px] font-semibold text-slate-400 hover:text-slate-200 transition-colors">
                                    {showAll
                                        ? t('buyerActivity.hideBrowsing', 'Hide browsing steps')
                                        : t('buyerActivity.showBrowsing', 'Show {{n}} more browsing steps', { n: hidden })}
                                </button>
                            )}
                        </>
                    )}

                    {/* THE LIVE FEED — the continuation of the same story:
                        appended at the bottom, under the historical timeline
                        which is already oldest → newest. "Happening now" stays
                        the last thing on the screen. */}
                    {(watching || feed.length > 0) && (
                        <div data-testid="buyer-activity-live-feed">
                            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-rose-400/80 mb-2.5 flex items-center gap-2">
                                {t('liveActivity.happeningNow', 'Happening now')}
                                {watching && <ListeningDots className={ticking ? '' : 'opacity-40'} />}
                            </p>
                            {feed.length === 0 ? (
                                <p className="text-[13px] text-slate-500" data-testid="buyer-activity-live-waiting">
                                    {t('liveActivity.waiting', 'Listening — their next tap will appear here.')}
                                </p>
                            ) : (
                                <ul className="space-y-4">
                                    {feed.map((it, i) => {
                                        const detail = it.has_detail ? detailFor(it, t) : null;
                                        const wip = !!it.in_progress;
                                        return (
                                            <li key={`live-${it.id}-${i}`}
                                                data-testid={`buyer-activity-live-item-${it.event_type}`}
                                                data-inprogress={wip ? '1' : '0'}
                                                className={`border-l-2 pl-3 -ml-3 animate-in fade-in slide-in-from-bottom-2 duration-500 ${
                                                    wip ? 'border-amber-500/60' : 'border-rose-500/50'}`}>
                                                <div className="flex items-baseline gap-2">
                                                    <span className={`flex-1 min-w-0 text-[15px] font-semibold leading-snug ${
                                                        wip ? 'text-amber-100' : 'text-white'}`}>
                                                        {labelFor(it, t)}
                                                    </span>
                                                    <span className={`text-[12px] shrink-0 whitespace-nowrap flex items-center gap-1 ${
                                                        wip ? 'text-amber-300/80' : 'text-rose-300/80'}`}
                                                        title={exact(it.at)}>
                                                        {wip && <Clock className="w-3 h-3" data-testid="buyer-activity-live-inprogress" />}
                                                        {wip ? t('buyerActivity.inProgress', 'in progress · {{when}}', { when: rel(it.at, t) }) : rel(it.at, t)}
                                                    </span>
                                                </div>
                                                {detail && (
                                                    <p className="mt-1.5 ml-0.5 pl-3 border-l border-white/10 text-[13px] text-slate-400 leading-relaxed">
                                                        {detail}
                                                    </p>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            <div ref={feedEndRef} />
                        </div>
                    )}

                    <p className="text-[11px] text-slate-600 leading-relaxed pt-4 border-t border-white/5">
                        {t('buyerActivity.privacyNote', 'Insurance enquiries appear as a bare fact only — the content of an insurance conversation is never shown here.')}
                    </p>
                </div>

                {/* Live tail without hijacking the scroll: only shown when new
                    actions landed while the salesperson was reading further up. */}
                {unseen > 0 && (
                    <div className="sticky bottom-24 z-20 flex justify-center pointer-events-none">
                        <button type="button" onClick={() => toBottom()}
                            data-testid="buyer-activity-new-activity"
                            className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full
                                       bg-rose-600 hover:bg-rose-500 px-4 min-h-[40px] text-[12px] font-bold
                                       text-white shadow-lg shadow-rose-900/40 transition-colors">
                            {t('liveActivity.newActivity', 'New activity')}
                            <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}
            </aside>
        </div>
    );
}
